import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import AdminLogin from './AdminLogin';
import { LogOut, User, Clock, Download, Save, Plus } from 'lucide-react';
import { servicesData, galleryData } from '../staticData';

const AdminPanel = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loginTime, setLoginTime] = useState(null);
  const [services, setServices] = useState(servicesData);
  const [gallery, setGallery] = useState(galleryData);
  const [activeTab, setActiveTab] = useState('services');

  // Check authentication status on component mount
  useEffect(() => {
    const checkAuth = () => {
      const isLoggedIn = localStorage.getItem('wow_admin_logged_in') === 'true';
      const loginTimeStamp = localStorage.getItem('wow_admin_login_time');
      
      if (isLoggedIn && loginTimeStamp) {
        const loginDate = new Date(parseInt(loginTimeStamp));
        const currentTime = new Date();
        const hoursSinceLogin = (currentTime - loginDate) / (1000 * 60 * 60);
        
        // Session expires after 24 hours
        if (hoursSinceLogin < 24) {
          setIsAuthenticated(true);
          setLoginTime(loginDate);
        } else {
          // Session expired
          localStorage.removeItem('wow_admin_logged_in');
          localStorage.removeItem('wow_admin_login_time');
        }
      }
      
      setIsLoading(false);
    };

    checkAuth();
  }, []);

  const handleLogin = () => {
    setIsAuthenticated(true);
    const loginTime = new Date();
    setLoginTime(loginTime);
  };

  const handleLogout = () => {
    localStorage.removeItem('wow_admin_logged_in');
    localStorage.removeItem('wow_admin_login_time');
    setIsAuthenticated(false);
    setLoginTime(null);
  };

  const updateService = (id, field, value) => {
    setServices(services.map(service => 
      service.id === id ? { ...service, [field]: value } : service
    ));
  };

  const updateGalleryItem = (id, field, value) => {
    setGallery(gallery.map(item => 
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  const addNewService = () => {
    const newService = {
      id: `service-${Date.now()}`,
      title: 'New Service',
      description: 'Enter service description...',
      icon: 'sparkles',
      features: ['Feature 1', 'Feature 2', 'Feature 3'],
      price_range: 'Contact for pricing',
      active: true,
      created_at: new Date().toISOString()
    };
    setServices([...services, newService]);
  };

  const deleteService = (id) => {
    if (window.confirm('Are you sure you want to delete this service?')) {
      setServices(services.filter(service => service.id !== id));
    }
  };

  const addNewGalleryItem = () => {
    const newItem = {
      id: `gallery-${Date.now()}`,
      title: 'New Project',
      description: 'Enter project description...',
      before_image: 'https://via.placeholder.com/600x400?text=Before+Image',
      after_image: 'https://via.placeholder.com/600x400?text=After+Image',
      category: 'collision',
      featured: false,
      created_at: new Date().toISOString()
    };
    setGallery([...gallery, newItem]);
  };

  const deleteGalleryItem = (id) => {
    if (window.confirm('Are you sure you want to delete this gallery item?')) {
      setGallery(gallery.filter(item => item.id !== id));
    }
  };

  const exportData = () => {
    const updatedContent = `// Auto-generated staticData.js - Updated ${new Date().toISOString()}

export const servicesData = ${JSON.stringify(services, null, 2)};

export const galleryData = ${JSON.stringify(gallery, null, 2)};

export const testimonials = [
  {
    id: 1,
    name: "Sarah Johnson",
    text: "Outstanding service! My car looks better than it did before the accident. The team was professional and kept me updated throughout the process.",
    rating: 5
  },
  {
    id: 2,
    name: "Mike Rodriguez",
    text: "Fast, reliable, and high-quality work. They worked directly with my insurance company which made everything so much easier.",
    rating: 5
  },
  {
    id: 3,
    name: "Jennifer Chen",
    text: "I was amazed at how well they matched the paint color. You can't even tell where the repair was done. Highly recommend!",
    rating: 5
  }
];

export const aboutInfo = {
  story: "Founded in 2010, WOW AUTOBODY has been serving the community with exceptional auto body repair services. Our commitment to quality craftsmanship and customer satisfaction has made us the trusted choice for thousands of satisfied customers.",
  mission: "To provide the highest quality auto body repair services while maintaining exceptional customer service and fair pricing. We treat every vehicle as if it were our own.",
  stats: [
    { label: "Years in Business", value: "14+" },
    { label: "Vehicles Repaired", value: "15,000+" },
    { label: "Customer Satisfaction", value: "98%" },
    { label: "Insurance Partners", value: "25+" }
  ]
};`;

    const blob = new Blob([updatedContent], { type: 'text/javascript' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'staticData.js';
    a.click();
    URL.revokeObjectURL(url);
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading admin panel...</p>
        </div>
      </div>
    );
  }

  // Show login form if not authenticated
  if (!isAuthenticated) {
    return <AdminLogin onLogin={handleLogin} />;
  }

  return (
    <div className="max-w-6xl mx-auto p-6 bg-gray-50 min-h-screen">
      
      {/* Header with Logout */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">WOW AUTOBODY Admin</h1>
            <p className="text-gray-600">Content Management System</p>
            <div className="flex items-center mt-2 space-x-4">
              <Badge className="bg-green-100 text-green-800">
                <User className="w-3 h-3 mr-1" />
                Logged in as Admin
              </Badge>
              {loginTime && (
                <Badge className="bg-blue-100 text-blue-800">
                  <Clock className="w-3 h-3 mr-1" />
                  Since {loginTime.toLocaleTimeString()}
                </Badge>
              )}
            </div>
          </div>
          
          <div className="flex items-center space-x-4">
            <Button onClick={exportData} className="bg-green-600 hover:bg-green-700">
              <Download className="w-4 h-4 mr-2" />
              Export Updated Content
            </Button>
            <Button onClick={handleLogout} variant="outline" className="text-red-600 border-red-300 hover:bg-red-50">
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white rounded-lg shadow-sm mb-6">
        <div className="flex border-b">
          <button
            onClick={() => setActiveTab('services')}
            className={`px-6 py-4 font-medium transition-colors ${
              activeTab === 'services' 
                ? 'border-b-2 border-blue-600 text-blue-600 bg-blue-50' 
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            Services ({services.length})
          </button>
          <button
            onClick={() => setActiveTab('gallery')}
            className={`px-6 py-4 font-medium transition-colors ${
              activeTab === 'gallery' 
                ? 'border-b-2 border-blue-600 text-blue-600 bg-blue-50' 
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            Gallery ({gallery.length})
          </button>
        </div>
      </div>

      {/* Services Tab */}
      {activeTab === 'services' && (
        <div>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Manage Services</h2>
            <Button onClick={addNewService} className="bg-blue-600 hover:bg-blue-700">
              <Plus className="w-4 h-4 mr-2" />
              Add New Service
            </Button>
          </div>
          
          <div className="space-y-6">
            {services.map((service, index) => (
              <Card key={service.id} className="p-6 hover:shadow-md transition-shadow">
                <CardContent className="p-0 space-y-4">
                  <div className="flex justify-between items-start">
                    <h3 className="text-lg font-bold text-gray-900">Service #{index + 1}</h3>
                    <Button 
                      onClick={() => deleteService(service.id)}
                      variant="destructive" 
                      size="sm"
                    >
                      Delete
                    </Button>
                  </div>
                  
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Service Title</label>
                      <Input
                        value={service.title}
                        onChange={(e) => updateService(service.id, 'title', e.target.value)}
                        placeholder="Enter service title"
                        className="text-lg"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                      <Textarea
                        value={service.description}
                        onChange={(e) => updateService(service.id, 'description', e.target.value)}
                        placeholder="Enter service description"
                        rows={3}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Price Range</label>
                      <Input
                        value={service.price_range}
                        onChange={(e) => updateService(service.id, 'price_range', e.target.value)}
                        placeholder="e.g., $500 - $2,000 or Contact for pricing"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Gallery Tab */}
      {activeTab === 'gallery' && (
        <div>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Manage Gallery</h2>
            <Button onClick={addNewGalleryItem} className="bg-blue-600 hover:bg-blue-700">
              <Plus className="w-4 h-4 mr-2" />
              Add New Project
            </Button>
          </div>
          
          <div className="space-y-6">
            {gallery.map((item, index) => (
              <Card key={item.id} className="p-6 hover:shadow-md transition-shadow">
                <CardContent className="p-0 space-y-4">
                  <div className="flex justify-between items-start">
                    <h3 className="text-lg font-bold text-gray-900">Project #{index + 1}</h3>
                    <Button 
                      onClick={() => deleteGalleryItem(item.id)}
                      variant="destructive" 
                      size="sm"
                    >
                      Delete
                    </Button>
                  </div>
                  
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Project Title</label>
                      <Input
                        value={item.title}
                        onChange={(e) => updateGalleryItem(item.id, 'title', e.target.value)}
                        placeholder="Enter project title"
                        className="text-lg"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                      <Textarea
                        value={item.description}
                        onChange={(e) => updateGalleryItem(item.id, 'description', e.target.value)}
                        placeholder="Enter project description"
                        rows={2}
                      />
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Before Image URL</label>
                        <Input
                          value={item.before_image}
                          onChange={(e) => updateGalleryItem(item.id, 'before_image', e.target.value)}
                          placeholder="https://example.com/before.jpg"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">After Image URL</label>
                        <Input
                          value={item.after_image}
                          onChange={(e) => updateGalleryItem(item.id, 'after_image', e.target.value)}
                          placeholder="https://example.com/after.jpg"
                        />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;